import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import kotlin.js.Date

private fun nowIso(): String = Date().toISOString()

private fun formatDateTime(iso: String?): String {
    if (iso.isNullOrBlank()) return "—"
    return formatDate(iso.substringBefore("T"))
}

/**
 * Admin "Obsah webu" (spec 10.6): two tabs, both editing HTML via the shared ContentEditor.
 * `openNewsId` opens that news item straight in the editor (Statistiky -> "Vytvořit aktualitu").
 */
@Composable
fun ContentAdminScreen(token: String?, role: String?, openNewsId: String? = null) {
    var tab by remember { mutableStateOf("news") }

    Div(attrs = { classes("card") }) {
        H2 { Text("Obsah webu") }
        if (!role.canManage()) {
            Text("Editace obsahu je dostupná jen adminovi/superadminovi.")
            return@Div
        }
        Div(attrs = { classes("actions") }) {
            Button(attrs = { if (tab == "news") classes("active"); onClick { tab = "news" } }) { Text("Aktuality") }
            Button(attrs = { if (tab == "pages") classes("active"); onClick { tab = "pages" } }) { Text("Stránky") }
        }
    }

    if (role.canManage()) {
        when (tab) {
            "pages" -> PagesTab(token, role)
            "news" -> NewsTab(token, role, openNewsId)
        }
    }
}

// ---------- Pages ----------

@Composable
private fun PagesTab(token: String?, role: String?) {
    var pages by remember { mutableStateOf<List<ContentPageDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var refreshKey by remember { mutableStateOf(0) }
    // null = list view; else editing this page (blank id = new page)
    var editing by remember { mutableStateOf<ContentPageDto?>(null) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(refreshKey) {
        loading = true
        try {
            pages = Api.fetchContentPages(token)
            error = null
        } catch (e: Throwable) {
            error = "Načtení stránek selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    val target = editing
    if (target != null) {
        key(target.id.ifBlank { "new" }) {
            PageEditForm(
                token = token,
                page = target,
                onCancel = { editing = null },
                onSaved = { editing = null; refreshKey++ },
            )
        }
        return
    }

    Div(attrs = { classes("card") }) {
        Button(attrs = {
            onClick {
                editing = ContentPageDto(id = "", slug = "", title = "", bodyHtml = "", menuOrder = 100, isPublished = false)
            }
        }) { Text("Nová stránka") }
        if (loading) Text("Načítám…")
        error?.let { Div { Text("Chyba: $it") } }

        Table {
            Tr {
                Th { Text("Název") }
                Th { Text("Slug") }
                Th { Text("Pořadí") }
                Th { Text("Publikováno") }
                Th { }
            }
            pages.forEach { p ->
                Tr {
                    Td { Text(p.title) }
                    Td { Text("/stranka/${p.slug}") }
                    Td { Text(p.menuOrder.toString()) }
                    Td { Text(if (p.isPublished) "ano" else "koncept") }
                    Td {
                        Button(attrs = { onClick { editing = p } }) { Text("Upravit") }
                        // Published content: admin may only hide it; deleting is superadmin-only (spec 10.6).
                        if (p.isPublished) {
                            Button(attrs = {
                                onClick {
                                    scope.launch {
                                        try {
                                            Api.updateContentPage(token, p.id, ContentPageInput(p.slug, p.title, p.bodyHtml, p.menuOrder, false))
                                            refreshKey++
                                        } catch (e: Throwable) {
                                            error = "Skrytí selhalo: ${e.apiMessage()}"
                                        }
                                    }
                                }
                            }) { Text("Skrýt") }
                        }
                        if (!p.isPublished || role.isSuperadmin()) {
                            Button(attrs = {
                                onClick {
                                    scope.launch {
                                        try {
                                            Api.deleteContentPage(token, p.id)
                                            refreshKey++
                                        } catch (e: Throwable) {
                                            error = "Smazání selhalo: ${e.apiMessage()}"
                                        }
                                    }
                                }
                            }) { Text("Smazat") }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun PageEditForm(token: String?, page: ContentPageDto, onCancel: () -> Unit, onSaved: () -> Unit) {
    var title by remember { mutableStateOf(page.title) }
    var slug by remember { mutableStateOf(page.slug) }
    var menuOrder by remember { mutableStateOf(page.menuOrder.toString()) }
    var published by remember { mutableStateOf(page.isPublished) }
    var body by remember { mutableStateOf(page.bodyHtml) }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    val isNew = page.id.isBlank()

    Div(attrs = { classes("card") }) {
        H3 { Text(if (isNew) "Nová stránka" else "Úprava stránky") }

        Div {
            Label { Text("Název: ") }
            Input(type = InputType.Text) {
                classes("wide-input")
                value(title)
                onInput {
                    title = it.value
                    if (isNew) slug = slugify(it.value)
                }
            }
        }
        Div {
            Label { Text("Slug (URL): ") }
            Input(type = InputType.Text) { value(slug); onInput { slug = it.value } }
            Span(attrs = { classes("user-badge") }) { Text("/stranka/${slug.ifBlank { "…" }}") }
        }
        Div {
            Label { Text("Pořadí v menu: ") }
            Input(type = InputType.Text) { value(menuOrder); onInput { menuOrder = it.value.filter { c -> c.isDigit() } } }
        }
        Div {
            Label {
                CheckboxInput(checked = published) { onInput { published = it.value } }
                Text(" Publikováno")
            }
        }

        H3 { Text("Obsah") }
        ContentEditor(token = token, initialHtml = page.bodyHtml, onHtmlChange = { body = it })

        Div(attrs = { classes("save-action") }) {
            Button(attrs = {
                if (saving || title.isBlank() || slug.isBlank()) disabled()
                onClick {
                    saving = true
                    error = null
                    scope.launch {
                        try {
                            val input = ContentPageInput(slug.trim(), title.trim(), body, menuOrder.toIntOrNull() ?: 0, published)
                            if (isNew) Api.createContentPage(token, input) else Api.updateContentPage(token, page.id, input)
                            onSaved()
                        } catch (e: Throwable) {
                            error = "Uložení selhalo: ${e.apiMessage()}"
                            saving = false
                        }
                    }
                }
            }) { Text(if (saving) "Ukládám…" else "Uložit") }
            Button(attrs = { onClick { onCancel() } }) { Text("Zpět") }
        }
        error?.let { Div(attrs = { classes("editor-error") }) { Text(it) } }
    }
}

// ---------- News ----------

@Composable
private fun NewsTab(token: String?, role: String?, openNewsId: String?) {
    var items by remember { mutableStateOf<List<NewsItemDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var refreshKey by remember { mutableStateOf(0) }
    var editing by remember { mutableStateOf<NewsItemDto?>(null) }
    // null = all sources; else a news_item.source value (NEWS_SOURCES).
    var sourceFilter by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(refreshKey) {
        loading = true
        try {
            items = Api.fetchNews(token)
            error = null
        } catch (e: Throwable) {
            error = "Načtení aktualit selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    LaunchedEffect(openNewsId) {
        if (openNewsId == null) return@LaunchedEffect
        try {
            editing = Api.fetchNewsItem(token, openNewsId)
        } catch (e: Throwable) {
            error = "Načtení aktuality selhalo: ${e.apiMessage()}"
        }
    }

    val target = editing
    if (target != null) {
        key(target.id.ifBlank { "new" }) {
            NewsEditForm(
                token = token,
                item = target,
                onCancel = { editing = null },
                onSaved = { editing = null; refreshKey++ },
            )
        }
        return
    }

    Div(attrs = { classes("card") }) {
        Button(attrs = {
            onClick {
                editing = NewsItemDto(id = "", title = "", bodyHtml = "", isPinned = false, publishedAt = null)
            }
        }) { Text("Nová aktualita") }
        Div(attrs = { classes("actions") }) {
            Button(attrs = { if (sourceFilter == null) classes("active"); onClick { sourceFilter = null } }) { Text("Vše") }
            NEWS_SOURCES.forEach { (source, label) ->
                Button(attrs = {
                    if (sourceFilter == source) classes("active")
                    onClick { sourceFilter = source }
                }) { Text(label) }
            }
        }
        if (loading) Text("Načítám…")
        error?.let { Div { Text("Chyba: $it") } }

        Table {
            Tr {
                Th { Text("Titulek") }
                Th { Text("Zdroj") }
                Th { Text("Datum") }
                Th { Text("Připnuto") }
                Th { Text("Publikováno") }
                Th { }
            }
            items.filter { sourceFilter == null || it.source == sourceFilter }.forEach { n ->
                Tr {
                    Td { Text(n.title) }
                    Td { Text(NEWS_SOURCES.firstOrNull { it.first == n.source }?.second ?: n.source) }
                    Td { Text(formatDateTime(n.publishedAt)) }
                    Td { Text(if (n.isPinned) "📌" else "") }
                    Td { Text(if (n.publishedAt != null) "ano" else "koncept") }
                    Td {
                        val published = n.publishedAt != null
                        Button(attrs = { onClick { editing = n } }) { Text("Upravit") }
                        // Published news: admin may only hide; deleting is superadmin-only (spec 10.6).
                        if (published) {
                            Button(attrs = {
                                onClick {
                                    scope.launch {
                                        try {
                                            Api.updateNews(token, n.id, NewsItemInput(n.title, n.bodyHtml, n.isPinned, null))
                                            refreshKey++
                                        } catch (e: Throwable) {
                                            error = "Skrytí selhalo: ${e.apiMessage()}"
                                        }
                                    }
                                }
                            }) { Text("Skrýt") }
                        }
                        if (!published || role.isSuperadmin()) {
                            Button(attrs = {
                                onClick {
                                    scope.launch {
                                        try {
                                            Api.deleteNews(token, n.id)
                                            refreshKey++
                                        } catch (e: Throwable) {
                                            error = "Smazání selhalo: ${e.apiMessage()}"
                                        }
                                    }
                                }
                            }) { Text("Smazat") }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun NewsEditForm(token: String?, item: NewsItemDto, onCancel: () -> Unit, onSaved: () -> Unit) {
    var title by remember { mutableStateOf(item.title) }
    var pinned by remember { mutableStateOf(item.isPinned) }
    var published by remember { mutableStateOf(item.publishedAt != null) }
    var body by remember { mutableStateOf(item.bodyHtml) }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    val isNew = item.id.isBlank()

    Div(attrs = { classes("card") }) {
        H3 { Text(if (isNew) "Nová aktualita" else "Úprava aktuality") }

        Div {
            Label { Text("Titulek: ") }
            Input(type = InputType.Text) { classes("wide-input"); value(title); onInput { title = it.value } }
        }
        Div {
            Label {
                CheckboxInput(checked = published) { onInput { published = it.value } }
                Text(" Publikováno")
            }
            Label {
                CheckboxInput(checked = pinned) { onInput { pinned = it.value } }
                Text(" Připnout nahoru")
            }
        }

        H3 { Text("Obsah") }
        ContentEditor(token = token, initialHtml = item.bodyHtml, onHtmlChange = { body = it })

        Div(attrs = { classes("save-action") }) {
            Button(attrs = {
                if (saving || title.isBlank()) disabled()
                onClick {
                    saving = true
                    error = null
                    scope.launch {
                        try {
                            // Keep the original publish date when re-saving an already-published item.
                            val publishedAt = if (published) (item.publishedAt ?: nowIso()) else null
                            val input = NewsItemInput(title.trim(), body, pinned, publishedAt)
                            if (isNew) Api.createNews(token, input) else Api.updateNews(token, item.id, input)
                            onSaved()
                        } catch (e: Throwable) {
                            error = "Uložení selhalo: ${e.apiMessage()}"
                            saving = false
                        }
                    }
                }
            }) { Text(if (saving) "Ukládám…" else "Uložit") }
            Button(attrs = { onClick { onCancel() } }) { Text("Zpět") }
        }
        error?.let { Div(attrs = { classes("editor-error") }) { Text(it) } }
    }
}
